import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import {
  TbAlertTriangle,
  TbArrowLeft,
  TbCheck,
  TbChevronLeft,
  TbChevronRight,
  TbClock,
  TbExternalLink,
  TbFileText,
  TbInbox,
  TbLock,
  TbMessageCircle,
  TbNote,
  TbPaperclip,
  TbRefresh,
  TbRotateClockwise,
  TbSearch,
  TbSend,
  TbUser,
  TbX,
} from "react-icons/tb";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import "../assets/styles/disputes-inbox.css";

const PAGE_NAME = "Disputes";
const LIST_REFRESH_MS = 20000;
const THREAD_REFRESH_MS = 10000;
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_FILES = "image/jpeg,image/png,image/webp,image/gif,application/pdf";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;
  toast.error(error.response?.data?.message || fallbackMessage);
};

const QUEUES = [
  { value: "needs_reply", label: "Needs reply" },
  { value: "waiting_customer", label: "Waiting on customer" },
  { value: "mine", label: "Assigned to me" },
  { value: "unassigned", label: "Unassigned" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All" },
];

const PRIORITIES = ["low", "medium", "high", "urgent"];
const FINAL_STATUSES = ["resolved", "rejected", "closed"];

const STATUS_LABELS = {
  open: "Open",
  admin_review: "Needs reply",
  customer_reply_pending: "Waiting on customer",
  resolved: "Resolved",
  rejected: "Rejected",
  closed: "Closed",
};

// Quick replies for the most common situations. Edit freely.
const CANNED_REPLIES = [
  {
    label: "Ask for photos",
    text: "Thank you for letting us know, and we're sorry about this. Could you please share a few clear photos of the issue (including the packaging, if possible)? You can attach them right here in the chat.",
  },
  {
    label: "Looking into it",
    text: "Thank you for your patience. We're looking into this with our production team and will update you here shortly.",
  },
  {
    label: "Replacement arranged",
    text: "We've arranged a replacement for the affected items. We'll share the dispatch details here as soon as they're ready.",
  },
  {
    label: "Refund initiated",
    text: "We've initiated a refund for this issue. The amount will be credited to your original payment method within 5 to 7 business days.",
  },
  {
    label: "Sorry for the delay",
    text: "We're sorry for the delay in getting back to you. Thank you for bearing with us.",
  },
];

const formatLabel = (value) =>
  String(value || "-")
    .replace(/_/g, " ")
    .replace(/^\w/, (character) => character.toUpperCase());

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const formatDay = (value) => {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  return date.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

const formatTime = (value) =>
  new Date(value).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

const timeAgo = (value) => {
  if (!value) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60000));

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h`;
  return `${Math.floor(minutes / (60 * 24))}d`;
};

const formatWait = (minutes) => {
  if (!minutes) return "";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

const parseJson = (value) => {
  if (!value) return {};
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const isImage = (attachment) =>
  String(attachment.file_type || "").startsWith("image/") ||
  /\.(jpe?g|png|webp|gif)$/i.test(attachment.file_url || "");

const validateFiles = (files) => {
  if (files.length > MAX_FILES) return `You can attach up to ${MAX_FILES} files.`;
  const tooBig = files.find((file) => file.size > MAX_FILE_SIZE);
  if (tooBig) return `${tooBig.name} is larger than 10 MB.`;
  return null;
};

const Attachments = ({ attachments = [] }) => {
  if (!attachments.length) return null;

  return (
    <div className="dsk-attachments">
      {attachments.map((attachment) =>
        isImage(attachment) ? (
          <a
            key={attachment.attachment_id}
            href={attachment.file_url}
            target="_blank"
            rel="noreferrer"
            className="dsk-attachment-image"
            title={attachment.file_name}
          >
            <img src={attachment.file_url} alt={attachment.file_name || "Attachment"} loading="lazy" />
          </a>
        ) : (
          <a
            key={attachment.attachment_id}
            href={attachment.file_url}
            target="_blank"
            rel="noreferrer"
            className="dsk-attachment-file"
          >
            <TbFileText />
            <span>{attachment.file_name || "Attachment"}</span>
          </a>
        )
      )}
    </div>
  );
};

const Disputes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const myId = Number(authUser?.user_id) || null;

  const [queue, setQueue] = useState("needs_reply");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [disputes, setDisputes] = useState([]);
  const [queueCounts, setQueueCounts] = useState({});
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [listLoading, setListLoading] = useState(true);

  const [selectedId, setSelectedId] = useState(
    () => new URLSearchParams(location.search).get("dispute_id") || null
  );
  const [dispute, setDispute] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [showContext, setShowContext] = useState(false);

  const [composerMode, setComposerMode] = useState("reply");
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [resolveDialog, setResolveDialog] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");

  const [adminUsers, setAdminUsers] = useState([]);

  const threadEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const listRequestRef = useRef(0);
  const threadRequestRef = useRef(0);
  const lastMessageCountRef = useRef(0);

  const adminNames = useMemo(
    () => new Map(adminUsers.map((admin) => [Number(admin.user_id), admin.name])),
    [adminUsers]
  );

  /* ---------- Data ---------- */

  const fetchDisputes = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = listRequestRef.current + 1;
      listRequestRef.current = requestId;
      if (!silent) setListLoading(true);

      try {
        const res = await api.get("/admin/disputes", {
          params: { queue, search: search || undefined, page, limit: 20 },
          headers: getPermissionHeaders("view"),
        });
        if (requestId !== listRequestRef.current) return;

        setDisputes(res.data?.data || []);
        setQueueCounts(res.data?.queue_counts || {});
        setPagination(res.data?.pagination || { page: 1, totalPages: 1, total: 0, limit: 20 });
      } catch (error) {
        if (!silent) showRequestError(error, "Failed to load disputes.");
      } finally {
        if (requestId === listRequestRef.current) setListLoading(false);
      }
    },
    [queue, search, page]
  );

  const fetchDispute = useCallback(async (disputeId, { silent = false } = {}) => {
    if (!disputeId) return;
    const requestId = threadRequestRef.current + 1;
    threadRequestRef.current = requestId;
    if (!silent) setThreadLoading(true);

    try {
      const res = await api.get(`/admin/disputes/${disputeId}`, {
        headers: getPermissionHeaders("view"),
      });
      if (requestId !== threadRequestRef.current) return;
      setDispute(res.data?.data || null);
    } catch (error) {
      if (!silent) {
        showRequestError(error, "Failed to load this dispute.");
        setSelectedId(null);
      }
    } finally {
      if (requestId === threadRequestRef.current) setThreadLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  useEffect(() => {
    api
      .get("/admin/notifications/routing/options", { headers: getPermissionHeaders("view") })
      .then((res) => setAdminUsers(res.data?.data?.users || []))
      .catch(() => {});
  }, []);

  // Debounce search typing.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Deep links from notifications and emails (?dispute_id=12).
  useEffect(() => {
    const linkedId = new URLSearchParams(location.search).get("dispute_id");
    if (linkedId) setSelectedId(linkedId);
  }, [location.search]);

  useEffect(() => {
    if (!selectedId) {
      setDispute(null);
      return;
    }

    setDraft("");
    setFiles([]);
    setComposerMode("reply");
    lastMessageCountRef.current = 0;
    fetchDispute(selectedId);
  }, [selectedId, fetchDispute]);

  // Live updates while the page is visible.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      fetchDisputes({ silent: true });
    };
    const timer = setInterval(tick, LIST_REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchDisputes]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      fetchDispute(selectedId, { silent: true });
    };
    const timer = setInterval(tick, THREAD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [selectedId, fetchDispute]);

  // Keep the newest message in view when messages arrive.
  useEffect(() => {
    const count = dispute?.messages?.length || 0;
    if (count !== lastMessageCountRef.current) {
      lastMessageCountRef.current = count;
      threadEndRef.current?.scrollIntoView({ block: "end" });
    }
  }, [dispute?.messages?.length]);

  const openDispute = (disputeId) => {
    setSelectedId(String(disputeId));
    setShowContext(false);
    navigate(`/disputes?dispute_id=${disputeId}`, { replace: true });
  };

  const closeThread = () => {
    setSelectedId(null);
    navigate("/disputes", { replace: true });
  };

  const refreshAll = async () => {
    await Promise.all([fetchDisputes({ silent: true }), fetchDispute(selectedId, { silent: true })]);
  };

  /* ---------- Actions ---------- */

  const sendMessage = async () => {
    const message = draft.trim();
    if (!message && !files.length) {
      toast.error("Write a message or attach a file.");
      return;
    }

    const fileError = validateFiles(files);
    if (fileError) {
      toast.error(fileError);
      return;
    }

    const isInternal = composerMode === "note";
    const body = new FormData();
    body.append("message", message);
    body.append("is_internal_note", isInternal ? "true" : "false");
    files.forEach((file) => body.append("attachments", file));

    try {
      setSending(true);
      await api.post(`/admin/disputes/${dispute.dispute_id}/messages`, body, {
        headers: getPermissionHeaders("add"),
        timeout: files.length ? 60000 : undefined,
      });
      toast.success(isInternal ? "Internal note added." : "Reply sent. The customer has been emailed.");
      setDraft("");
      setFiles([]);
      await refreshAll();
    } catch (error) {
      showRequestError(error, "Failed to send.");
    } finally {
      setSending(false);
    }
  };

  const runAction = async (request, successMessage, fallbackMessage) => {
    try {
      setActionLoading(true);
      const res = await request();
      toast.success(res.data?.message || successMessage);
      await refreshAll();
      return true;
    } catch (error) {
      showRequestError(error, fallbackMessage);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const updatePriority = (priority) =>
    runAction(
      () =>
        api.put(
          `/admin/disputes/${dispute.dispute_id}/status`,
          { priority },
          { headers: getPermissionHeaders("edit") }
        ),
      "Priority updated.",
      "Failed to update priority."
    );

  const updateAssignee = (value) =>
    runAction(
      () =>
        api.put(
          `/admin/disputes/${dispute.dispute_id}/assign`,
          { assigned_to_user_id: value ? Number(value) : null },
          { headers: getPermissionHeaders("edit") }
        ),
      "Assignment updated.",
      "Failed to assign."
    );

  const reopen = () =>
    runAction(
      () => api.put(`/admin/disputes/${dispute.dispute_id}/reopen`, {}, { headers: getPermissionHeaders("edit") }),
      "Dispute reopened.",
      "Failed to reopen."
    );

  const submitResolution = async () => {
    if (resolutionNote.trim().length < 5) {
      toast.error("Explain the outcome to the customer.");
      return;
    }

    const done = await runAction(
      () =>
        api.put(
          `/admin/disputes/${dispute.dispute_id}/resolve`,
          { outcome: resolveDialog, resolution_note: resolutionNote.trim() },
          { headers: getPermissionHeaders("edit") }
        ),
      "Saved.",
      "Failed to save the outcome."
    );

    if (done) {
      setResolveDialog(null);
      setResolutionNote("");
    }
  };

  const addFiles = (event) => {
    const next = [...files, ...Array.from(event.target.files || [])];
    const error = validateFiles(next);
    if (error) toast.error(error);
    else setFiles(next);
    event.target.value = "";
  };

  const insertCanned = (text) => {
    setComposerMode("reply");
    setDraft((current) => (current.trim() ? `${current.trim()}\n\n${text}` : text));
  };

  /* ---------- Rendering helpers ---------- */

  const getSenderName = (message) => {
    if (message.sender_role === "customer") return dispute?.customer?.name || "Customer";
    if (Number(message.sender_id) === 0) return "Automated";
    if (myId && Number(message.sender_id) === myId) return "You";
    return adminNames.get(Number(message.sender_id)) || "Support team";
  };

  const assigneeName = (userId) =>
    userId ? adminNames.get(Number(userId)) || `Admin #${userId}` : "Unassigned";

  const thread = useMemo(() => {
    const items = [];
    let lastDay = null;

    (dispute?.messages || []).forEach((message) => {
      const day = new Date(message.createdAt).toDateString();
      if (day !== lastDay) {
        items.push({ type: "day", key: `day-${day}`, label: formatDay(message.createdAt) });
        lastDay = day;
      }
      items.push({ type: "message", key: message.message_id, message });
    });

    return items;
  }, [dispute?.messages]);

  const isFinal = dispute && FINAL_STATUSES.includes(dispute.status);
  const order = dispute?.order;
  const totalOverdue = queueCounts.overdue || 0;

  /* ---------- Render ---------- */

  return (
    <div className="admin-page dsk-page">
      <div className="page-header dsk-header">
        <div>
          <p>Customer Support Desk</p>
          <h1>Order Disputes</h1>
        </div>
        <div className="dsk-header-actions">
          {totalOverdue > 0 && (
            <button
              type="button"
              className="dsk-overdue-pill"
              onClick={() => {
                setQueue("needs_reply");
                setPage(1);
              }}
            >
              <TbAlertTriangle />
              {totalOverdue} overdue
            </button>
          )}
          <button
            type="button"
            className="dsk-icon-button"
            onClick={refreshAll}
            title="Refresh"
            aria-label="Refresh"
          >
            <TbRefresh />
          </button>
        </div>
      </div>

      <div className={`dsk-shell${selectedId ? " has-selection" : ""}${showContext ? " show-context" : ""}`}>
        {/* ===== Inbox list ===== */}
        <aside className="dsk-list-pane">
          <div className="dsk-search">
            <TbSearch />
            <input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search order no., customer, subject or #ID"
              aria-label="Search disputes"
            />
            {searchInput && (
              <button type="button" onClick={() => setSearchInput("")} aria-label="Clear search">
                <TbX />
              </button>
            )}
          </div>

          <div className="dsk-queues" role="tablist" aria-label="Dispute queues">
            {QUEUES.map((option) => (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={queue === option.value}
                className={`dsk-queue${queue === option.value ? " is-active" : ""}`}
                onClick={() => {
                  setQueue(option.value);
                  setPage(1);
                }}
              >
                {option.label}
                <span className="dsk-queue-count">{queueCounts[option.value] ?? 0}</span>
              </button>
            ))}
          </div>

          <div className="dsk-list">
            {listLoading && !disputes.length ? (
              Array.from({ length: 5 }, (_, index) => <div key={index} className="dsk-skeleton" />)
            ) : disputes.length === 0 ? (
              <div className="dsk-empty-list">
                <TbInbox />
                <strong>{search ? "No matches" : "All clear"}</strong>
                <span>
                  {search ? "Try a different search." : "Nothing in this queue right now."}
                </span>
              </div>
            ) : (
              disputes.map((row) => {
                const unread = row.unread_count > 0;
                const preview = row.last_message;

                return (
                  <button
                    key={row.dispute_id}
                    type="button"
                    className={`dsk-item${String(row.dispute_id) === String(selectedId) ? " is-selected" : ""}${
                      unread ? " is-unread" : ""
                    }`}
                    onClick={() => openDispute(row.dispute_id)}
                  >
                    <div className="dsk-item-top">
                      <span className="dsk-item-customer">
                        {unread && <span className="dsk-unread-dot" aria-label="Unread" />}
                        {row.customer?.name || "Customer"}
                      </span>
                      <span className="dsk-item-time">{timeAgo(preview?.createdAt || row.updatedAt)}</span>
                    </div>
                    <div className="dsk-item-subject">{row.subject}</div>
                    <div className="dsk-item-preview">
                      {preview
                        ? `${preview.sender_role === "admin" ? (preview.is_internal_note ? "Note: " : "You: ") : ""}${
                            preview.preview || "Attachment"
                          }`
                        : formatLabel(row.issue_type)}
                    </div>
                    <div className="dsk-item-tags">
                      <span className="dsk-tag">#{row.dispute_id}</span>
                      <span className="dsk-tag">{row.order?.order_number || `Order ${row.order_id}`}</span>
                      {["high", "urgent"].includes(row.priority) && (
                        <span className={`dsk-tag is-${row.priority}`}>{formatLabel(row.priority)}</span>
                      )}
                      {row.is_overdue ? (
                        <span className="dsk-tag is-overdue">
                          <TbClock /> {formatWait(row.wait_minutes)}
                        </span>
                      ) : (
                        queue === "all" && (
                          <span className={`dsk-tag is-status-${row.status}`}>{STATUS_LABELS[row.status]}</span>
                        )
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="dsk-list-footer">
              <span>
                {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div>
                <button
                  type="button"
                  className="dsk-icon-button"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                  aria-label="Previous page"
                >
                  <TbChevronLeft />
                </button>
                <button
                  type="button"
                  className="dsk-icon-button"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((current) => current + 1)}
                  aria-label="Next page"
                >
                  <TbChevronRight />
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* ===== Conversation ===== */}
        <section className="dsk-thread-pane">
          {!selectedId ? (
            <div className="dsk-thread-empty">
              <TbMessageCircle />
              <h3>Select a dispute</h3>
              <p>Pick a conversation from the list to read it and reply.</p>
            </div>
          ) : threadLoading && !dispute ? (
            <div className="dsk-thread-empty">
              <div className="dsk-spinner" />
              <p>Loading conversation…</p>
            </div>
          ) : dispute ? (
            <>
              <header className="dsk-thread-header">
                <button type="button" className="dsk-icon-button dsk-back" onClick={closeThread} aria-label="Back to list">
                  <TbArrowLeft />
                </button>
                <div className="dsk-thread-title">
                  <h2>{dispute.subject}</h2>
                  <p>
                    #{dispute.dispute_id} · {formatLabel(dispute.issue_type)} ·{" "}
                    {dispute.customer?.name || "Customer"} · {order?.order_number || `Order ${dispute.order_id}`}
                  </p>
                </div>
                <span className={`dsk-status is-${dispute.status}`}>{STATUS_LABELS[dispute.status]}</span>
                <button
                  type="button"
                  className={`dsk-icon-button dsk-context-toggle${showContext ? " is-active" : ""}`}
                  onClick={() => setShowContext((current) => !current)}
                  aria-label="Customer and order details"
                  title="Customer and order details"
                >
                  <TbUser />
                </button>
              </header>

              <div className="dsk-toolbar">
                <label className="dsk-field">
                  <span>Assignee</span>
                  <select
                    value={dispute.assigned_to_user_id || ""}
                    onChange={(event) => updateAssignee(event.target.value)}
                    disabled={actionLoading}
                  >
                    <option value="">Unassigned</option>
                    {myId && !adminNames.has(myId) && <option value={myId}>Me</option>}
                    {adminUsers.map((admin) => (
                      <option key={admin.user_id} value={admin.user_id}>
                        {Number(admin.user_id) === myId ? `${admin.name} (me)` : admin.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="dsk-field">
                  <span>Priority</span>
                  <select
                    value={dispute.priority}
                    onChange={(event) => updatePriority(event.target.value)}
                    disabled={actionLoading}
                    className={`is-${dispute.priority}`}
                  >
                    {PRIORITIES.map((priority) => (
                      <option key={priority} value={priority}>
                        {formatLabel(priority)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="dsk-toolbar-actions">
                  {isFinal ? (
                    <button type="button" className="dsk-button" onClick={reopen} disabled={actionLoading}>
                      <TbRotateClockwise /> Reopen
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="dsk-button is-quiet"
                        onClick={() => {
                          setResolutionNote("");
                          setResolveDialog("rejected");
                        }}
                        disabled={actionLoading}
                      >
                        <TbX /> Reject
                      </button>
                      <button
                        type="button"
                        className="dsk-button is-success"
                        onClick={() => {
                          setResolutionNote("");
                          setResolveDialog("resolved");
                        }}
                        disabled={actionLoading}
                      >
                        <TbCheck /> Resolve
                      </button>
                    </>
                  )}
                </div>
              </div>

              {dispute.is_overdue && (
                <div className="dsk-banner is-overdue">
                  <TbAlertTriangle />
                  The customer has been waiting {formatWait(dispute.wait_minutes)} for a reply.
                </div>
              )}

              <div className="dsk-thread" aria-live="polite">
                {thread.map((item) =>
                  item.type === "day" ? (
                    <div key={item.key} className="dsk-day">
                      <span>{item.label}</span>
                    </div>
                  ) : (
                    (() => {
                      const { message } = item;
                      const fromCustomer = message.sender_role === "customer";
                      const kind = fromCustomer ? "customer" : message.is_internal_note ? "note" : "admin";

                      return (
                        <div key={item.key} className={`dsk-message is-${kind}`}>
                          <div className="dsk-message-meta">
                            <strong>{getSenderName(message)}</strong>
                            {message.is_internal_note && (
                              <span className="dsk-note-badge">
                                <TbLock /> Internal note
                              </span>
                            )}
                            <time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
                          </div>
                          <div className="dsk-bubble">
                            {message.message && <p>{message.message}</p>}
                            <Attachments attachments={message.attachments} />
                          </div>
                        </div>
                      );
                    })()
                  )
                )}

                {isFinal && (
                  <div className={`dsk-outcome is-${dispute.status}`}>
                    {dispute.status === "resolved" ? <TbCheck /> : <TbX />}
                    <div>
                      <strong>
                        {STATUS_LABELS[dispute.status]}
                        {dispute.resolved_at ? ` · ${formatDateTime(dispute.resolved_at)}` : ""}
                      </strong>
                      {dispute.resolution_note && <p>{dispute.resolution_note}</p>}
                    </div>
                  </div>
                )}
                <div ref={threadEndRef} />
              </div>

              <div className={`dsk-composer is-${composerMode}`}>
                <div className="dsk-composer-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={composerMode === "reply"}
                    className={composerMode === "reply" ? "is-active" : ""}
                    onClick={() => setComposerMode("reply")}
                    disabled={isFinal}
                  >
                    <TbSend /> Reply to customer
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={composerMode === "note"}
                    className={composerMode === "note" ? "is-active" : ""}
                    onClick={() => setComposerMode("note")}
                  >
                    <TbNote /> Internal note
                  </button>
                  {composerMode === "reply" && !isFinal && (
                    <select
                      className="dsk-canned"
                      value=""
                      onChange={(event) => {
                        const reply = CANNED_REPLIES[Number(event.target.value)];
                        if (reply) insertCanned(reply.text);
                      }}
                      aria-label="Insert a quick reply"
                    >
                      <option value="">Quick replies…</option>
                      {CANNED_REPLIES.map((reply, index) => (
                        <option key={reply.label} value={index}>
                          {reply.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {isFinal && composerMode === "reply" ? (
                  <div className="dsk-composer-locked">
                    <TbLock /> This dispute is {STATUS_LABELS[dispute.status].toLowerCase()}. Reopen it to reply, or
                    add an internal note.
                  </div>
                ) : (
                  <>
                    <textarea
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if ((event.ctrlKey || event.metaKey) && event.key === "Enter") sendMessage();
                      }}
                      placeholder={
                        composerMode === "note"
                          ? "Only your team can see internal notes…"
                          : `Reply to ${dispute.customer?.name || "the customer"}. They'll also get it by email.`
                      }
                      rows={3}
                      maxLength={5000}
                      disabled={sending}
                    />

                    {files.length > 0 && (
                      <div className="dsk-file-chips">
                        {files.map((file, index) => (
                          <span key={`${file.name}-${index}`} className="dsk-file-chip">
                            <TbPaperclip />
                            {file.name}
                            <button
                              type="button"
                              onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                              aria-label={`Remove ${file.name}`}
                            >
                              <TbX />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="dsk-composer-footer">
                      <button
                        type="button"
                        className="dsk-icon-button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending || files.length >= MAX_FILES}
                        title="Attach photos or PDFs (up to 5, 10 MB each)"
                        aria-label="Attach files"
                      >
                        <TbPaperclip />
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_FILES}
                        multiple
                        hidden
                        onChange={addFiles}
                      />
                      <small>Ctrl + Enter to send</small>
                      <button
                        type="button"
                        className={`dsk-button ${composerMode === "note" ? "is-note" : "is-primary"}`}
                        onClick={sendMessage}
                        disabled={sending || (!draft.trim() && !files.length)}
                      >
                        {composerMode === "note" ? <TbNote /> : <TbSend />}
                        {sending ? "Sending…" : composerMode === "note" ? "Add note" : "Send reply"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : null}
        </section>

        {/* ===== Customer & order context ===== */}
        {dispute && selectedId && (
          <aside className="dsk-context-pane">
            <div className="dsk-context-head">
              <h3>Details</h3>
              <button
                type="button"
                className="dsk-icon-button"
                onClick={() => setShowContext(false)}
                aria-label="Close details"
              >
                <TbX />
              </button>
            </div>

            <section className="dsk-context-card">
              <h4>Customer</h4>
              <strong>{dispute.customer?.name || "Customer"}</strong>
              {dispute.customer?.email && <a href={`mailto:${dispute.customer.email}`}>{dispute.customer.email}</a>}
              {dispute.customer?.phone && <a href={`tel:${dispute.customer.phone}`}>{dispute.customer.phone}</a>}
            </section>

            <section className="dsk-context-card">
              <h4>Request</h4>
              <dl>
                <dt>Opened</dt>
                <dd>{formatDateTime(dispute.createdAt)}</dd>
                <dt>Assignee</dt>
                <dd>{assigneeName(dispute.assigned_to_user_id)}</dd>
                <dt>Waiting on</dt>
                <dd>{formatLabel(dispute.awaiting_reply_from)}</dd>
                <dt>Reminders</dt>
                <dd>
                  {dispute.admin_reminder_count || 0} to team · {dispute.customer_reminder_count || 0} to customer
                </dd>
              </dl>
            </section>

            {order && (
              <section className="dsk-context-card">
                <div className="dsk-context-row">
                  <h4>Order</h4>
                  <Link to={`/orders?order_id=${order.order_id}`} className="dsk-link">
                    Open <TbExternalLink />
                  </Link>
                </div>
                <strong>{order.order_number}</strong>
                <dl>
                  <dt>Status</dt>
                  <dd>{formatLabel(order.order_status)}</dd>
                  <dt>Payment</dt>
                  <dd>
                    {formatLabel(order.payment_status)}
                    {order.payment?.method ? ` · ${order.payment.method}` : ""}
                  </dd>
                  <dt>Total</dt>
                  <dd>{formatMoney(order.total_amount)}</dd>
                  <dt>Placed</dt>
                  <dd>{formatDateTime(order.createdAt)}</dd>
                </dl>
                <ul className="dsk-items">
                  {(order.items || []).map((item) => {
                    const snapshot = parseJson(item.product_snapshot);
                    return (
                      <li key={item.order_item_id}>
                        <img src={snapshot.image || "/logo192.png"} alt="" />
                        <div>
                          <span>{snapshot.name || "Product"}</span>
                          <small>
                            Qty {item.quantity} · {formatMoney(item.total_price)}
                          </small>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {dispute.previous_disputes?.length > 0 && (
              <section className="dsk-context-card">
                <h4>Earlier requests</h4>
                <ul className="dsk-history">
                  {dispute.previous_disputes.map((previous) => (
                    <li key={previous.dispute_id}>
                      <button type="button" onClick={() => openDispute(previous.dispute_id)}>
                        #{previous.dispute_id} · {previous.subject}
                      </button>
                      <small>{STATUS_LABELS[previous.status]}</small>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </aside>
        )}
      </div>

      <Dialog
        visible={Boolean(resolveDialog)}
        onHide={() => !actionLoading && setResolveDialog(null)}
        header={resolveDialog === "rejected" ? "Reject this dispute" : "Resolve this dispute"}
        className="dsk-dialog"
        modal
        draggable={false}
        style={{ width: "min(560px, 94vw)" }}
        footer={
          <div className="dsk-dialog-actions">
            <button
              type="button"
              className="dsk-button is-quiet"
              onClick={() => setResolveDialog(null)}
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`dsk-button ${resolveDialog === "rejected" ? "is-danger" : "is-success"}`}
              onClick={submitResolution}
              disabled={actionLoading || resolutionNote.trim().length < 5}
            >
              {resolveDialog === "rejected" ? <TbX /> : <TbCheck />}
              {actionLoading ? "Saving…" : resolveDialog === "rejected" ? "Reject & notify" : "Resolve & notify"}
            </button>
          </div>
        }
      >
        <p className="dsk-dialog-hint">
          {resolveDialog === "rejected"
            ? "Explain why the request can't be accepted. This message is posted in the conversation and emailed to the customer."
            : "Summarise what was done. This message is posted in the conversation and emailed to the customer, who can reopen it within 14 days by replying."}
        </p>
        <textarea
          className="dsk-dialog-textarea"
          rows={5}
          maxLength={5000}
          value={resolutionNote}
          onChange={(event) => setResolutionNote(event.target.value)}
          placeholder={
            resolveDialog === "rejected"
              ? "e.g. The order was printed exactly as approved in the proof you confirmed on…"
              : "e.g. We've dispatched replacement cards via Delhivery (AWB 1234). They should reach you by…"
          }
          autoFocus
        />
      </Dialog>
    </div>
  );
};

export default Disputes;
